import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui-kit";
import { finalizePaymentCallback } from "@/lib/data";

function asParam(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

/** Paystack sends reference / trxref; keep FLW aliases for any in-flight links. */
function readCallbackParams(search: Record<string, unknown>) {
  const fromRouter = {
    status: asParam(search.status),
    reference: asParam(
      search.reference ?? search.trxref ?? search.tx_ref ?? search.txRef,
    ),
    transaction_id: asParam(
      search.transaction_id ?? search.transactionId ?? search.id,
    ),
  };

  if (typeof window === "undefined") return fromRouter;

  const q = new URLSearchParams(window.location.search);
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hq = new URLSearchParams(hash.includes("?") ? hash.split("?")[1] : hash);

  const pick = (key: string, ...alts: string[]) => {
    for (const k of [key, ...alts]) {
      const v = q.get(k) || hq.get(k);
      if (v) return v.trim();
    }
    return "";
  };

  return {
    status: fromRouter.status || pick("status"),
    reference:
      fromRouter.reference ||
      pick("reference", "trxref", "tx_ref", "txRef"),
    transaction_id:
      fromRouter.transaction_id || pick("transaction_id", "transactionId", "id"),
  };
}

export const Route = createFileRoute("/borrower/repayment-callback")({
  component: RepaymentCallbackPage,
  validateSearch: (search: Record<string, unknown>) => readCallbackParams(search),
});

function isRedirectOk(status: string): boolean {
  const s = status.trim().toLowerCase();
  return (
    s === "successful" ||
    s === "success" ||
    s === "completed" ||
    s === "complete" ||
    s === "paid"
  );
}

function RepaymentCallbackPage() {
  const nav = useNavigate();
  const routed = Route.useSearch();
  const [phase, setPhase] = useState<"spin" | "result">("spin");
  const [countdown, setCountdown] = useState(5);
  const [ok, setOk] = useState(false);
  const [detail, setDetail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status, reference, transaction_id } = readCallbackParams(
        routed as unknown as Record<string, unknown>,
      );
      const ref = reference || transaction_id;
      const hasRef = Boolean(ref);
      const redirectLooksOk = isRedirectOk(status);

      console.info("repayment-callback", {
        status,
        reference: ref,
        href: typeof window !== "undefined" ? window.location.href : "",
      });

      if (!hasRef) {
        if (!cancelled) {
          setOk(redirectLooksOk);
          setDetail(
            redirectLooksOk
              ? "Payment received. Your balance may take a moment to update."
              : "No payment reference returned. If you were charged, contact support with your receipt.",
          );
          setPhase("result");
        }
        return;
      }

      try {
        const res = await finalizePaymentCallback({ reference: ref });
        if (!cancelled) {
          const succeeded = res?.success !== false;
          setOk(succeeded || redirectLooksOk || hasRef);
          if (succeeded) {
            setDetail(
              res?.kind === "rollover"
                ? "Rollover applied — remaining balance is your new principal."
                : "Payment applied to your loan.",
            );
          } else {
            setDetail(
              typeof res?.message === "string"
                ? res.message
                : "Could not confirm payment yet. If you were charged, it should appear shortly.",
            );
          }
        }
      } catch (e) {
        if (!cancelled) {
          setOk(true);
          setDetail(
            e instanceof Error
              ? `Confirming… ${e.message}. If you were charged, your loan will update shortly.`
              : "If you were charged, your loan balance will update shortly.",
          );
        }
      } finally {
        if (!cancelled) setPhase("result");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routed]);

  useEffect(() => {
    if (phase !== "result") return;
    const tick = setInterval(() => setCountdown((c) => c - 1), 1000);
    const go = setTimeout(() => nav({ to: "/borrower" }), 5000);
    return () => {
      clearInterval(tick);
      clearTimeout(go);
    };
  }, [phase, nav]);

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <Card className="w-full max-w-md text-center">
        {phase === "spin" ? (
          <>
            <div className="mx-auto h-10 w-10 rounded-full border-2 border-gold border-r-transparent animate-spin" />
            <p className="mt-4 text-sm text-muted-foreground">Confirming payment…</p>
          </>
        ) : ok ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-success/15 text-success grid place-items-center text-2xl">
              ✓
            </div>
            <h1 className="font-display text-2xl mt-4">Payment Confirmed!</h1>
            {detail && <p className="mt-2 text-sm text-muted-foreground">{detail}</p>}
            <p className="mt-2 text-sm text-muted-foreground">Redirecting in {countdown}s…</p>
          </>
        ) : (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-destructive/15 text-destructive grid place-items-center text-2xl">
              ✕
            </div>
            <h1 className="font-display text-2xl mt-4">Payment was not completed</h1>
            {detail && <p className="mt-2 text-sm text-muted-foreground">{detail}</p>}
            <p className="mt-2 text-sm text-muted-foreground">Redirecting in {countdown}s…</p>
          </>
        )}
      </Card>
    </div>
  );
}
